import {
    AI_ADDON_AUTO_REPLY_FEATURE_FLAG,
    AI_ADDON_MESSAGE_SUGGESTION_FEATURE_FLAG,
    AI_ADDON_REPHRASE_FEATURE_FLAG,
    AI_ADDON_TRIGGER_FEATURE_FLAG,
    AI_FEATURE_STATUS_CONTROL_TYPES,
} from "../constants/aiFeatureConstants";

export interface AiFeatureCatalogRow {
    featureId?: string;
    featureName?: string;
    featureKey?: string;
    featureFlag?: number;
    description?: string;
    creditUnit?: string;
    credits?: number | string | null;
    enabled?: boolean | number;
    statusControlType?: string;
    /** {@code PricingFeaturesEntity.displayInSettings} from GET /ai/features/list. */
    displayInSettings?: boolean;
    timeStamp?: string | number;
}

export const normalizeAiFeatureEnabled = (enabled: boolean | number | undefined): boolean =>
    enabled === true || enabled === 1;

export const isAiFeatureVisibleInListing = (featureRow: AiFeatureCatalogRow): boolean =>
    Boolean(featureRow.displayInSettings);

/** Trigger (Custom Trigger) is always on — users cannot disable it from AI Features listing. */
export const isAlwaysEnabledAiFeatureRow = (featureRow: AiFeatureCatalogRow): boolean =>
    Number(featureRow.featureFlag) === AI_ADDON_TRIGGER_FEATURE_FLAG;

export const resolveAiFeatureStatusControlType = (featureRow: AiFeatureCatalogRow): string => {
    const statusControlType =
        typeof featureRow.statusControlType === "string" ? featureRow.statusControlType.trim() : "";
    return statusControlType || AI_FEATURE_STATUS_CONTROL_TYPES.TOGGLE;
};

export const parseFeaturesListResponse = (response: unknown): AiFeatureCatalogRow[] => {
    if (Array.isArray(response)) {
        return response as AiFeatureCatalogRow[];
    }
    if (response != null && typeof response === "object") {
        return [response as AiFeatureCatalogRow];
    }
    return [];
};

export const findFeatureByFlag = (
    catalog: AiFeatureCatalogRow[],
    featureFlag: number
): AiFeatureCatalogRow | undefined =>
    catalog.find((featureRow) => Number(featureRow.featureFlag) === featureFlag);

/** Resolve catalog rows from API fields only — no client-side feature ids. */
export const findRephraseFeatureRow = (catalog: AiFeatureCatalogRow[]): AiFeatureCatalogRow | undefined =>
    findFeatureByFlag(catalog, AI_ADDON_REPHRASE_FEATURE_FLAG);

export const findMessageSuggestionFeatureRow = (
    catalog: AiFeatureCatalogRow[]
): AiFeatureCatalogRow | undefined =>
    findFeatureByFlag(catalog, AI_ADDON_MESSAGE_SUGGESTION_FEATURE_FLAG);

export const findAutoReplyFeatureRow = (catalog: AiFeatureCatalogRow[]): AiFeatureCatalogRow | undefined =>
    findFeatureByFlag(catalog, AI_ADDON_AUTO_REPLY_FEATURE_FLAG);

export const findCallSummaryFeatureRow = (catalog: AiFeatureCatalogRow[]): AiFeatureCatalogRow | undefined =>
    catalog.find((featureRow) => featureRow.featureKey?.trim().toLowerCase() === "ai_call");

export const computeIsFeatureEnabledForCatalogRow = (
    catalog: AiFeatureCatalogRow[],
    catalogLoaded: boolean,
    selectFeatureRow: (catalogRows: AiFeatureCatalogRow[]) => AiFeatureCatalogRow | undefined
): boolean => {
    if (!catalogLoaded) {
        return false;
    }
    const featureRow = selectFeatureRow(catalog);
    if (!featureRow) {
        return false;
    }
    if (featureRow.featureFlag != null) {
        const canonical = findFeatureByFlag(catalog, Number(featureRow.featureFlag));
        return canonical ? normalizeAiFeatureEnabled(canonical.enabled) : false;
    }
    return normalizeAiFeatureEnabled(featureRow.enabled);
};

export const getCatalogFeatureNames = (catalog: AiFeatureCatalogRow[]): Set<string> =>
    new Set(
        catalog
            .map((featureRow) => featureRow.featureName?.trim())
            .filter((featureName): featureName is string => Boolean(featureName))
    );
